import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import closeIcon from "../../../assets/images/close.png";
import SherComboBox from "../../../components/ComboBoxes/SherComboBox";
import { useDbContext } from "../../../context/DbContext";

function EventDetails({ openReport, closeFindDialog, onClose, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();

  const getCurrentDate = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [fromDate, setFromDate] = useState(getCurrentDate());
  const [toDate, setToDate] = useState(getCurrentDate());
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedActions, setSelectedActions] = useState(["1", "2", "3"]);

  const [allForms, setAllForms] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const userRef = useRef(null);
  const formRef = useRef(null);

  // Fetch dropdown lists
  useEffect(() => {
    if (!dbCode) return;

    const fetchForms = async () => {
      try {
        const res = await axios.get(`${apiBaseUrl}/common/selectAllformDatas/${dbCode}`);
        const forms = (res.data || []).map((item) => ({
          value: item.fmrAbbreviation || item.fmrEntryNo,
          label: item.fmrAbbreviation || `Form ${item.fmrEntryNo}`,
        }));
        setAllForms(forms);
      } catch (err) {
        console.error("Error fetching forms list:", err);
      }
    };

    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${apiBaseUrl}/common/selectallusers/${dbCode}`);
        const users = (res.data || []).map((item) => ({
          value: item.agentCode || item.Auto || item.UserID,
          label: item.userName || item.Name || `User #${item.agentCode}`,
        }));
        setAllUsers(users);
      } catch (err) {
        console.error("Error fetching users list:", err);
      }
    };

    fetchForms();
    fetchUsers();
  }, [apiBaseUrl, dbCode]);

  // Handle action checkbox toggle
  const handleActionToggle = (val, isChecked) => {
    if (val === "ALL") {
      setSelectedActions(isChecked ? ["1", "2", "3"] : []);
    } else {
      setSelectedActions((prev) =>
        isChecked ? [...prev, val] : prev.filter((a) => a !== val)
      );
    }
  };

  const isAllActionsSelected =
    selectedActions.length === 3 &&
    selectedActions.includes("1") &&
    selectedActions.includes("2") &&
    selectedActions.includes("3");

  const handleShowReport = () => {
    const payload = {
      fromDate,
      toDate,
      user: selectedUser,
      form: selectedForm,
      action: selectedActions.join(","),
    };
    if (openReport) {
      openReport(payload);
    }
  };

  const handleClose = (e) => {
    if (onClose) onClose(e);
    else if (closeFindDialog) closeFindDialog(e);
  };

  const inputClass =
    "w-full px-2 text-xs border border-slate-300 rounded-[5px] focus:outline-none focus:ring-1 focus:ring-blue-500 font-Inter font-semibold text-slate-800 bg-white transition-all placeholder-slate-400";

  return (
    <div
      className="bg-[#bbd1ea] w-[420px] rounded-xl shadow-2xl flex flex-col items-center absolute transition-all duration-300 font-Inter select-none p-2 border border-blue-200"
      style={{ zIndex }}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleShowReport();
        if (e.key === "Escape") handleClose(e);
      }}
    >
      {/* Header Bar */}
      <div className="w-full flex items-center justify-between pb-2 px-1">
        <div className="flex items-center gap-2">
          <img src={headeLogo} alt="Logo" className="h-5 w-auto" />
          <span className="text-xs font-bold text-slate-800 tracking-wide">
            Event Details Parameters
          </span>
        </div>
        <button
          onClick={handleClose}
          className="rounded p-1 bg-red-500 hover:bg-red-600 border-none h-5 w-5 flex justify-center items-center transition cursor-pointer"
          title="Close Dialog"
        >
          <img src={closeIcon} alt="X" className="h-2.5 w-2.5 invert" />
        </button>
      </div>

      {/* Main Body Card */}
      <div className="bg-white w-full rounded-lg shadow-sm border border-slate-200 flex flex-col gap-3 p-3.5">
        {/* Date Range Section */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex items-center justify-between gap-2">
          <div className="flex-1 flex flex-col gap-1">
            <label className="text-[11px] font-bold text-slate-600">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full h-[28px] px-2 text-xs border border-slate-300 rounded-[5px] bg-white text-slate-800 font-medium focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div className="flex-1 flex flex-col gap-1">
            <label className="text-[11px] font-bold text-slate-600">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full h-[28px] px-2 text-xs border border-slate-300 rounded-[5px] bg-white text-slate-800 font-medium focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Filter Selection Fields */}
        <div className="flex flex-col gap-2.5">
          {/* User Selection */}
          <div className="flex items-center gap-3">
            <label className="text-xs font-bold text-slate-700 w-24 shrink-0">
              User:
            </label>
            <div className="flex-1">
              <SherComboBox
                comboRef={userRef}
                options={allUsers}
                value={selectedUser?.label || ""}
                placeholder="All Users"
                onSelect={(opt) => setSelectedUser(opt)}
                onClear={() => setSelectedUser(null)}
                className="w-full"
                inputClassName={inputClass}
                inputHeight="28px"
              />
            </div>
          </div>

          {/* Form Selection */}
          <div className="flex items-center gap-3">
            <label className="text-xs font-bold text-slate-700 w-24 shrink-0">
              Form / Module:
            </label>
            <div className="flex-1">
              <SherComboBox
                comboRef={formRef}
                options={allForms}
                value={selectedForm?.label || ""}
                placeholder="All Forms"
                onSelect={(opt) => setSelectedForm(opt)}
                onClear={() => setSelectedForm(null)}
                className="w-full"
                inputClassName={inputClass}
                inputHeight="28px"
              />
            </div>
          </div>

          {/* Action Checkboxes */}
          <div className="flex items-center gap-3">
            <label className="text-xs font-bold text-slate-700 w-24 shrink-0">
              Actions:
            </label>
            <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg p-2 flex items-center justify-between text-xs">
              <label className="flex items-center gap-1.5 cursor-pointer font-bold text-slate-800">
                <input
                  type="checkbox"
                  checked={isAllActionsSelected}
                  onChange={(e) => handleActionToggle("ALL", e.target.checked)}
                  className="rounded text-blue-600 focus:ring-blue-500"
                />
                All
              </label>

              <label className="flex items-center gap-1 cursor-pointer text-emerald-800 font-semibold">
                <input
                  type="checkbox"
                  checked={selectedActions.includes("1")}
                  onChange={(e) => handleActionToggle("1", e.target.checked)}
                  className="rounded text-emerald-600 focus:ring-emerald-500"
                />
                Save
              </label>

              <label className="flex items-center gap-1 cursor-pointer text-amber-800 font-semibold">
                <input
                  type="checkbox"
                  checked={selectedActions.includes("2")}
                  onChange={(e) => handleActionToggle("2", e.target.checked)}
                  className="rounded text-amber-600 focus:ring-amber-500"
                />
                Edit
              </label>

              <label className="flex items-center gap-1 cursor-pointer text-rose-800 font-semibold">
                <input
                  type="checkbox"
                  checked={selectedActions.includes("3")}
                  onChange={(e) => handleActionToggle("3", e.target.checked)}
                  className="rounded text-rose-600 focus:ring-rose-500"
                />
                Delete
              </label>
            </div>
          </div>
        </div>

        {/* Buttons Row */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            onClick={handleClose}
            className="px-4 py-1.5 rounded-md text-xs font-bold text-slate-600 hover:bg-slate-100 transition cursor-pointer border border-slate-300"
          >
            Cancel
          </button>
          <button
            onClick={handleShowReport}
            className="px-5 py-1.5 rounded-md text-xs font-bold text-white bg-[#1e73be] hover:bg-blue-700 shadow-sm transition cursor-pointer flex items-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Show Report
          </button>
        </div>
      </div>
    </div>
  );
}

export default EventDetails;